// ---------------------------------------------------------------------------
// The public lobby browser.
//
// In-progress rooms stay listed rather than being filtered out, because
// spectating one is a real thing to want. The state column is what tells the two
// apart, and the action follows the state: `Join` where there is a seat,
// `Spectate` where there is not.
//
// One row, two layouts. The phone card and the desktop table are the same markup
// reflowed with `lg:contents`, so a row cannot end up saying two different
// things at two different widths.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useRef, useState } from 'react';
import type { PublicRoomSummary } from '@countdown/shared';
import { useNavigate } from 'react-router-dom';
import { listPublicRooms } from '../lib/api.js';
import { AppHeader } from '../components/layout/AppHeader.js';
import {
  Button,
  LetterTile,
  RoundSequenceSummary,
  Segmented,
  type SegmentedOption,
} from '../components/primitives/index.js';

/**
 * The public summary carries no round index yet, so the state of an in-progress
 * room degrades to `In progress` rather than inventing a number. When the server
 * adds one, `Round 2 of 5` starts appearing here with no other change.
 */
type RoomSummary = PublicRoomSummary & { currentRoundIndex?: number };

type Filter = 'all' | 'waiting' | 'space';

const FILTERS: readonly SegmentedOption<Filter>[] = [
  { value: 'all', label: 'All' },
  { value: 'waiting', label: 'Waiting' },
  { value: 'space', label: 'Has room for me' },
];

/** Often enough that a room appears while you are looking at the list. */
const POLL_INTERVAL_MS = 8_000;

/**
 * Whether somebody clicking this row would get a SEAT rather than a view.
 *
 * Three things can take the seat away and they are different facts: the game has
 * already started, the room has a limit and has hit it, or the host has locked
 * the door. `maxPlayers: null` is the default now and means no limit, so a null
 * cap is a seat rather than a missing one — reading it as a number would have
 * `4 < null` come out false and quietly mark every ordinary room as full.
 */
function hasSeat(room: RoomSummary): boolean {
  if (room.inProgress || room.locked) return false;
  return room.maxPlayers === null || room.playerCount < room.maxPlayers;
}

function matchesFilter(room: RoomSummary, filter: Filter): boolean {
  if (filter === 'waiting') return !room.inProgress;
  if (filter === 'space') return hasSeat(room);
  return true;
}

function stateLabel(room: RoomSummary): string {
  if (room.phase === 'game-over') return 'Between games';
  if (!room.inProgress) return 'Waiting';
  if (typeof room.currentRoundIndex === 'number') {
    return `Round ${room.currentRoundIndex + 1} of ${room.roundSequence.length}`;
  }
  return 'In progress';
}

function reasonFrom(error: unknown): string {
  return error instanceof Error && error.message ? error.message : 'Could not reach the server.';
}

export function BrowseScreen() {
  const navigate = useNavigate();
  const mounted = useRef(true);

  const [rooms, setRooms] = useState<RoomSummary[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');

  const load = useCallback(async () => {
    try {
      const list = await listPublicRooms();
      if (!mounted.current) return;
      setRooms(list);
      setError(null);
    } catch (caught) {
      if (mounted.current) setError(reasonFrom(caught));
    } finally {
      if (mounted.current) setLoaded(true);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    let timer: ReturnType<typeof setInterval> | null = null;

    const start = () => {
      if (timer === null) timer = setInterval(() => void load(), POLL_INTERVAL_MS);
    };
    const stop = () => {
      if (timer !== null) clearInterval(timer);
      timer = null;
    };

    // A hidden tab is one nobody is reading: polling it spends the player's
    // battery and the server's time on a list that will be refetched the moment
    // they come back anyway.
    const onVisibilityChange = () => {
      if (document.hidden) stop();
      else {
        void load();
        start();
      }
    };

    queueMicrotask(() => void load());
    if (!document.hidden) start();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      mounted.current = false;
      stop();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [load]);

  const visible = rooms.filter((room) => matchesFilter(room, filter));
  // The empty state carries its own Create, so the phone footer stands down
  // rather than putting a second copy of it on the same screen.
  const showEmpty = loaded && rooms.length === 0;

  return (
    <div className="flex min-h-full flex-col">
      <AppHeader />

      <main className="mx-auto flex w-full max-w-[1180px] flex-1 flex-col px-4 py-6 sm:px-8 lg:py-10">
        <div className="flex items-center gap-3">
          <h1 className="text-[20px] font-bold tracking-[-0.02em] lg:text-[28px]">Public games</h1>
          {/* The same badge the home screen puts on the button that leads here,
              and the same reserved slot: it holds its size before the first list
              arrives, so the heading does not jump when it does. Nothing is
              claimed until there is something to claim — an unloaded list is not
              an empty one. */}
          <span
            aria-hidden={!loaded || undefined}
            className={`grid h-6 min-w-6 place-items-center rounded-full bg-blue-700/40 px-1.5 font-mono text-[12px] font-semibold text-blue-200 tabular transition-opacity duration-200 ${
              loaded ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <span aria-hidden="true">{rooms.length}</span>
            <span className="sr-only">{rooms.length} open</span>
          </span>
        </div>

        <div className="pt-4">
          <Segmented options={FILTERS} value={filter} onChange={setFilter} label="Filter public games" />
        </div>

        {error !== null && (
          <div
            role="alert"
            className="mt-4 flex flex-wrap items-center gap-3 rounded-card border border-alert bg-surface px-4 py-3"
          >
            <p className="text-[15px] text-alert">{error}</p>
            <Button variant="outline" size="sm" onClick={() => void load()}>
              Try again
            </Button>
          </div>
        )}

        {!loaded && error === null && (
          <p className="pt-6 text-[15px] text-ink-dim">Loading the list.</p>
        )}

        {showEmpty && <EmptyState />}

        {loaded && rooms.length > 0 && visible.length === 0 && (
          <div className="flex flex-col items-start gap-3 pt-8">
            <p className="text-[17px] text-ink-muted">No game matches that filter.</p>
            <Button variant="outline" size="md" onClick={() => setFilter('all')}>
              Show all games
            </Button>
          </div>
        )}

        {visible.length > 0 && (
          <div className="pt-5 lg:pt-7">
            <div className="hidden lg:grid lg:grid-cols-[250px_120px_1fr_150px_150px] lg:items-center lg:gap-4 lg:border-b lg:border-line lg:px-7 lg:pb-2.5">
              <span className="label-mono">host</span>
              <span className="label-mono">players</span>
              <span className="label-mono">rounds</span>
              <span className="label-mono">state</span>
              <span />
            </div>

            <ul className="flex flex-col gap-2.5 lg:gap-0">
              {visible.map((room) => (
                <RoomRow
                  key={room.roomCode}
                  room={room}
                  onJoin={() => navigate(`/room/${room.roomCode}`)}
                  // The room page reads this and joins as a spectator instead of
                  // taking a seat.
                  onSpectate={() =>
                    navigate(`/room/${room.roomCode}`, { state: { asSpectator: true } })
                  }
                />
              ))}
            </ul>
          </div>
        )}

        {!showEmpty && (
          <div className="mt-auto pt-8 lg:hidden">
            {/* Creating a room, solo or not, needs a display name, and the name
                field lives on the home screen. Handing off there beats a second
                copy of it. */}
            <Button variant="primary" size="lg" block onClick={() => navigate('/')}>
              Create a room
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}

function RoomRow({
  room,
  onJoin,
  onSpectate,
}: {
  room: RoomSummary;
  onJoin: () => void;
  onSpectate: () => void;
}) {
  const seat = hasSeat(room);

  return (
    <li className="flex flex-col gap-3 rounded-card border border-line bg-surface-2 px-4 py-3.5 transition-colors duration-150 lg:grid lg:grid-cols-[250px_120px_1fr_150px_150px] lg:items-center lg:gap-4 lg:rounded-none lg:border-x-0 lg:border-t-0 lg:bg-transparent lg:px-7 lg:py-4 lg:hover:bg-surface-2">
      <div className="flex items-center gap-2.5 lg:contents">
        {/* No handle slot: the public summary carries no username, and a `guest`
            chip would claim something we do not know. */}
        <div className="flex items-center gap-2.5">
          <span
            aria-hidden="true"
            className="size-[9px] shrink-0 rounded-full bg-blue-400"
          />
          <span className="truncate text-[17px] font-semibold">{room.hostName}</span>
        </div>

        <div className="ml-auto font-mono text-[15px] font-semibold tabular lg:ml-0 lg:text-[17px]">
          {room.playerCount}
          {/* No denominator when the room has no limit, which is the default:
              `12/` reads as a missing number and `12/5000` advertises a cap
              nobody set. The count on its own is the whole truth there. */}
          {room.maxPlayers === null ? null : (
            <span className="text-ink-dim">/{room.maxPlayers}</span>
          )}
          <span className="sr-only"> players</span>
        </div>
      </div>

      <div className="flex items-center gap-2.5 lg:contents">
        <div className={`items-center gap-2 ${room.inProgress ? 'hidden lg:flex' : 'flex'}`}>
          <RoundSequenceSummary sequence={room.roundSequence} />
          {room.teamsEnabled && (
            <span className="font-mono text-[12px] text-ink-dim">teams</span>
          )}
        </div>

        <div className={`items-center gap-2 ${room.inProgress ? 'flex' : 'hidden lg:flex'}`}>
          <span
            aria-hidden="true"
            className={`size-[7px] shrink-0 rounded-full ${
              room.inProgress ? 'bg-accent' : 'bg-good'
            }`}
          />
          <span className="text-[13px] font-medium text-ink lg:text-[14px]">
            {stateLabel(room)}
          </span>
          {room.inProgress && room.teamsEnabled && (
            <span className="font-mono text-[12px] text-ink-dim lg:hidden">teams on</span>
          )}
        </div>

        <div className="ml-auto lg:ml-0">
          {seat ? (
            <Button variant="primary" size="md" block onClick={onJoin}>
              Join
            </Button>
          ) : (
            <Button variant="outline" size="md" block onClick={onSpectate}>
              Spectate
            </Button>
          )}
        </div>
      </div>
    </li>
  );
}

function EmptyState() {
  const navigate = useNavigate();

  return (
    <div className="mt-6 flex flex-col items-center gap-4 rounded-frame border border-dashed border-line-strong bg-bg-alt px-6 py-12 lg:py-[52px]">
      <div className="flex gap-1.5 opacity-50" aria-hidden="true">
        <LetterTile state="face-down" size="phone" />
        <LetterTile state="face-down" size="phone" />
        <LetterTile state="face-down" size="phone" />
      </div>
      <h2 className="text-center text-[22px] font-bold tracking-[-0.02em] lg:text-[26px]">
        No public games right now
      </h2>
      {/* A new room is private, so "start one and it appears here" would be a
          lie. The lobby's visibility switch is what puts a room on this page. */}
      <p className="max-w-[420px] text-center text-[16px] leading-relaxed text-ink-muted">
        A room is private until its lobby lists it here.
      </p>
      <div className="pt-2">
        <Button variant="primary" size="md" onClick={() => navigate('/')}>
          Create a room
        </Button>
      </div>
    </div>
  );
}
